/**
 * useClaimRewards — executes fee claim transaction via clanker-sdk claimRewards.
 *
 * Full lifecycle: preparing → awaiting_signature → pending → confirmed | failed | rejected
 * On success: invalidates available-rewards + creator fees caches, tracks PostHog event.
 *
 * Story 6.3 — Task 2
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { formatUnits } from "viem";
import { getPublicClient, getWalletClient } from "wagmi/actions";

import { trackClientEvent } from "../../lib/analytics";
import { getChainAddresses } from "../../lib/chain-addresses";
import { mapChainError } from "../../lib/chain-errors";
import { getActiveChain, wagmiConfig } from "../../lib/wagmi-config";
import { creatorKeys } from "../api/query-keys";
import { useWallet } from "../use-wallet";
import type { TransactionStateActions } from "./use-transaction-state";
import { useTransactionState } from "./use-transaction-state";

function isHexAddress(value: string): value is `0x${string}` {
	return /^0x[a-fA-F0-9]{40}$/.test(value);
}

/**
 * Safely extract error details from ClankerError or plain error objects.
 * ClankerError has .data.type/.data.rawName, but mocks may use flat shape { type, code, message }.
 */
function extractErrorInfo(error: unknown): { type: string; rawName: string; message: string } {
	if (error instanceof Error && "data" in error) {
		// ClankerError: extends Error, has .data = { type, rawName, label }
		const { data } = error;
		if (typeof data === "object" && data !== null) {
			return {
				type: "type" in data && typeof data.type === "string" ? data.type : "unknown",
				rawName: "rawName" in data && typeof data.rawName === "string" ? data.rawName : "unknown",
				message: error.message,
			};
		}
	}
	if (typeof error === "object" && error !== null) {
		// Plain object shape: { type, code, message }
		return {
			type: "type" in error && typeof error.type === "string" ? error.type : "unknown",
			rawName: "code" in error && typeof error.code === "string" ? error.code : "unknown",
			message:
				"message" in error && typeof error.message === "string" ? error.message : "Unknown error",
		};
	}
	return { type: "unknown", rawName: "unknown", message: String(error) };
}

function isUserRejection(info: { type: string; message: string }): boolean {
	return info.type === "user_rejected" || info.message.includes("user rejected");
}

function isNoFeesToClaim(info: { rawName: string; message: string }): boolean {
	return info.rawName === "NoFeesToClaim" || info.message.includes("No fees to claim");
}

/** Extract the RPC URL from a viem transport object without type assertions */
function extractTransportUrl(transport: unknown): string | undefined {
	if (typeof transport !== "object" || transport === null || !("url" in transport)) {
		return undefined;
	}
	// After the `in` check, TS knows `url` exists on `transport`
	const obj: { url: unknown } = transport;
	return typeof obj.url === "string" ? obj.url : undefined;
}

export type UseClaimRewardsReturn = TransactionStateActions & {
	claim: (tokenAddress: string, rewardRecipient: string, tokenId: string) => Promise<void>;
};

export function useClaimRewards(): UseClaimRewardsReturn {
	const txState = useTransactionState();
	const { setPreparing, setAwaitingSignature, setPending, setConfirmed, setFailed, setRejected } =
		txState;
	const { address } = useWallet();
	const queryClient = useQueryClient();

	const claim = useCallback(
		async (tokenAddress: string, rewardRecipient: string, tokenId: string) => {
			try {
				setPreparing();

				if (!tokenAddress || !rewardRecipient) {
					setFailed("Missing token address or reward recipient.");
					return;
				}

				if (!isHexAddress(tokenAddress) || !isHexAddress(rewardRecipient)) {
					setFailed("Invalid address format.");
					return;
				}

				const activeChain = getActiveChain();
				const walletClient = await getWalletClient(wagmiConfig, {
					chainId: activeChain.id,
				}).catch(() => null);
				if (!walletClient) {
					setFailed("Wallet client not available. Please reconnect your wallet.");
					return;
				}

				const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });
				if (!publicClient) {
					setFailed("Network connection unavailable. Please check your connection.");
					return;
				}

				const chainAddresses = getChainAddresses(activeChain.id);
				if (!chainAddresses) {
					setFailed(`Unsupported chain: ${activeChain.id}`);
					return;
				}

				setAwaitingSignature();

				// Detect Anvil fork: check if RPC URL is localhost
				const rpcUrl = extractTransportUrl(publicClient.transport);
				const isAnvilFork =
					!!rpcUrl && (rpcUrl.includes("localhost") || rpcUrl.includes("127.0.0.1"));

				let result: { txHash: `0x${string}` | undefined; error?: unknown };

				if (isAnvilFork) {
					// On Anvil fork, use impersonation + unsigned tx (same as fork tests).
					// MetaMask's signed eth_sendRawTransaction reverts on Anvil while
					// unsigned eth_sendTransaction (impersonated) succeeds — Anvil fork quirk.
					await fetch(rpcUrl, {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							jsonrpc: "2.0",
							method: "anvil_impersonateAccount",
							params: [rewardRecipient],
							id: 1,
						}),
					});

					const { createWalletClient: viemCreateWalletClient, http: viemHttp } = await import(
						"viem"
					);
					const impersonatedWallet = viemCreateWalletClient({
						account: rewardRecipient,
						chain: publicClient.chain,
						transport: viemHttp(rpcUrl),
					});

					const { Clanker } = await import("@repo/clanker-sdk/v4");
					const clankerImpersonated = new Clanker({
						wallet: impersonatedWallet,
						publicClient,
					});

					result = await clankerImpersonated.claimRewards({
						token: chainAddresses.weth,
						rewardRecipient: rewardRecipient,
					});

					// Stop impersonation
					await fetch(rpcUrl, {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							jsonrpc: "2.0",
							method: "anvil_stopImpersonatingAccount",
							params: [rewardRecipient],
							id: 1,
						}),
					});
				} else {
					// Production / test path: use the normal wallet client from wagmi
					const { Clanker } = await import("@repo/clanker-sdk/v4");
					const clanker = new Clanker({
						wallet: walletClient,
						publicClient,
					});

					// FeeLocker.claim(rewardRecipient, token) — token is the fee currency (WETH),
					// NOT the ERC-20 token address. Fees are collected in the pool's base currency.
					result = await clanker.claimRewards({
						token: chainAddresses.weth,
						rewardRecipient: rewardRecipient,
					});
				}

				if (result.error || !result.txHash) {
					if (result.error) {
						const errorInfo = extractErrorInfo(result.error);
						if (isUserRejection(errorInfo)) {
							setRejected();
						} else if (isNoFeesToClaim(errorInfo)) {
							setFailed("No fees available to claim at this time.");
						} else {
							setFailed(mapChainError(errorInfo.message));
						}
					} else {
						setFailed("Transaction failed — no hash returned.");
					}
					return;
				}

				const txHash = result.txHash;
				setPending(txHash);

				// Wait for transaction receipt
				const receipt = await publicClient.waitForTransactionReceipt({
					hash: txHash,
				});

				if (receipt.status === "reverted") {
					setFailed("Transaction reverted on-chain. Please try again.");
					return;
				}

				setConfirmed();

				// Read the pre-claim balance for analytics (may be up to 30s stale; "unknown" if cache expired)
				const rewardsCache = queryClient.getQueryData<{ amount: bigint }>([
					"chain",
					"available-rewards",
					tokenAddress,
					rewardRecipient,
				]);
				const claimedAmount = rewardsCache?.amount ?? 0n;
				const claimedFormatted = claimedAmount ? formatUnits(claimedAmount, 18) : "unknown";

				// Track analytics event before cache invalidation to avoid stale reads
				trackClientEvent("fee_claimed", {
					tokenId,
					amount: claimedFormatted,
					creatorWallet: rewardRecipient,
					txHash,
				});

				// Optimistic update: inject the new claim into the creator fees cache
				// so it appears immediately in the Claim History UI (the backend indexer
				// may not have indexed the event yet, or in dev mode uses seeded data).
				const feesQueryKey = creatorKeys.fees(tokenId, address ?? rewardRecipient);
				type CreatorFeesData = {
					accumulated: {
						contractAddress: string;
						availableAmount: string;
						totalClaimedAmount: string;
						lastClaimTimestamp: string | null;
					} | null;
					history: Array<{
						txHash: string;
						timestamp: string;
						tokenAddress: string;
						claimAmount: string;
						creatorWallet: string;
					}>;
					meta: { dataSource: string; dataAge: number };
				};
				const existingFees = queryClient.getQueryData<CreatorFeesData>(feesQueryKey);
				const newClaim = {
					txHash,
					timestamp: new Date().toISOString(),
					tokenAddress,
					claimAmount: claimedAmount.toString(),
					creatorWallet: rewardRecipient,
				};
				queryClient.setQueryData<CreatorFeesData>(feesQueryKey, {
					accumulated: existingFees?.accumulated
						? {
								...existingFees.accumulated,
								availableAmount: "0",
								totalClaimedAmount: (
									BigInt(existingFees.accumulated.totalClaimedAmount) + claimedAmount
								).toString(),
								lastClaimTimestamp: new Date().toISOString(),
							}
						: {
								contractAddress: tokenAddress,
								availableAmount: "0",
								totalClaimedAmount: claimedAmount.toString(),
								lastClaimTimestamp: new Date().toISOString(),
							},
					history: [newClaim, ...(existingFees?.history ?? [])],
					meta: existingFees?.meta ?? { dataSource: "optimistic", dataAge: 0 },
				});

				// Invalidate available-rewards (on-chain balance should now be 0)
				await queryClient.invalidateQueries({
					queryKey: ["chain", "available-rewards", tokenAddress, rewardRecipient],
				});
			} catch (error: unknown) {
				const errorInfo = extractErrorInfo(error);
				if (isUserRejection(errorInfo)) {
					setRejected();
				} else {
					setFailed(mapChainError(error));
				}
			}
		},
		[
			setPreparing,
			setAwaitingSignature,
			setPending,
			setConfirmed,
			setFailed,
			setRejected,
			address,
			queryClient,
		]
	);

	return useMemo(
		() => ({
			...txState,
			claim,
		}),
		[txState, claim]
	);
}
